// src/components/HaberFormu.js

import React, { useState, useEffect } from 'react';
import { 
    Calendar, Camera, Check, X, FileText, 
    AlignLeft, Sparkles, Building
} from 'lucide-react';
import { callApi } from '../api';
import ImageUploader from './ImageUploader';
import AutoTextarea from './AutoTextarea';

const HaberFormu = ({ haberToEdit, kulup, onSave, onCancel }) => {
    const initialState = {
        baslik: '',
        aciklama: '',
        icerik: '',
        resimURL: '',
        tarih: ''
    };

    const [formData, setFormData] = useState(initialState);
    const [showUploader, setShowUploader] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (haberToEdit) {
            setFormData({
                baslik: haberToEdit.baslik || '',
                aciklama: haberToEdit.aciklama || '',
                icerik: haberToEdit.icerik || '',
                resimURL: haberToEdit.resimURL || haberToEdit.resimUrl || '',
                tarih: haberToEdit.tarih ? new Date(haberToEdit.tarih).toISOString().slice(0, 16) : ''
            });
        } else {
            const nowIso = new Date(Date.now() - (new Date().getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
            setFormData({ ...initialState, tarih: nowIso });
        }
    }, [haberToEdit]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setError(null);

        try {
            const kulupQuery = kulup?.kulupID ? `?kulupID=${kulup.kulupID}` : '';
            const finalData = {
                ...formData,
                resimUrl: formData.resimURL,
                kulupID: kulup?.kulupID
            };

            if (haberToEdit) {
                await callApi(`/haberler${kulupQuery}`, 'PUT', { ...finalData, haberID: haberToEdit.haberID });
            } else {
                await callApi(`/haberler${kulupQuery}`, 'POST', finalData);
            }
            onSave();
        } catch (err) {
            setError(err.message || 'Haber kaydedilirken bir hata oluştu.');
        } finally {
            setIsSaving(false);
        }
    };

    const wordCount = formData.icerik.trim() ? formData.icerik.trim().split(/\s+/).length : 0;
    const charCount = formData.icerik.length;

    return (
        <form onSubmit={handleSubmit} className="modern-news-editor">
            <style>{`
                .modern-news-editor {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                    color: #0f172a;
                    font-family: 'Inter', -apple-system, sans-serif;
                }

                .news-header-bar {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border-bottom: 1px solid #f1f5f9;
                    padding-bottom: 12px;
                }

                .news-header-title {
                    margin: 0;
                    font-size: 1.25rem;
                    font-weight: 800;
                    color: #0f172a;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .news-close-btn {
                    background: #f1f5f9;
                    border: none;
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    color: #64748b;
                    transition: all 0.2s;
                }
                .news-close-btn:hover {
                    background: #e2e8f0;
                    color: #0f172a;
                }

                /* 16:9 Cover Banner */
                .news-banner-wrap {
                    position: relative;
                    width: 100%;
                    aspect-ratio: 16 / 9;
                    border-radius: 18px;
                    overflow: hidden;
                    background: #f1f5f9;
                    border: 1px solid #e2e8f0;
                }
                .news-banner-wrap img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
                .news-banner-overlay {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.6) 100%);
                    display: flex;
                    align-items: flex-end;
                    justify-content: flex-end;
                    padding: 14px;
                }
                .news-banner-btn {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    background: rgba(255, 255, 255, 0.95);
                    backdrop-filter: blur(8px);
                    color: #0f172a;
                    border: none;
                    padding: 8px 14px;
                    border-radius: 20px;
                    font-size: 0.8rem;
                    font-weight: 700;
                    cursor: pointer;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
                    transition: all 0.2s ease;
                }
                .news-banner-btn:hover {
                    background: #ffffff;
                    transform: translateY(-1px);
                }

                .news-uploader-drawer {
                    background: #ffffff;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 16px;
                    padding: 16px;
                    animation: fadeIn 0.2s ease;
                }

                /* Meta Bar */
                .news-meta-strip {
                    display: flex;
                    gap: 12px;
                    align-items: center;
                    flex-wrap: wrap;
                }
                .news-date-card {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 8px 14px;
                    font-size: 0.85rem;
                }
                .news-date-card input {
                    border: none;
                    background: transparent;
                    font-size: 0.85rem;
                    font-weight: 600;
                    color: #334155;
                    outline: none;
                    font-family: inherit;
                }
                .news-club-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    background: rgba(99, 102, 241, 0.08);
                    color: var(--primary, #6366f1);
                    padding: 8px 14px;
                    border-radius: 12px;
                    font-size: 0.82rem;
                    font-weight: 700;
                }

                /* Title Input */
                .news-headline-input {
                    width: 100%;
                    border: none;
                    background: transparent;
                    font-size: clamp(22px, 4vw, 30px);
                    font-weight: 800;
                    color: #0f172a;
                    outline: none;
                    padding: 4px 0;
                    font-family: inherit;
                    line-height: 1.25;
                }
                .news-headline-input::placeholder {
                    color: #cbd5e1;
                }

                /* Cards */
                .news-section-card {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 16px;
                    padding: 16px 18px;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
                }
                .news-section-label {
                    font-size: 0.8rem;
                    font-weight: 700;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    margin-bottom: 8px;
                }

                .news-word-counter {
                    display: flex;
                    justify-content: flex-end;
                    gap: 12px;
                    font-size: 0.74rem;
                    color: #94a3b8;
                    margin-top: 8px;
                    font-weight: 600;
                }
            `}</style>

            <div className="drag-handle-pill"></div>

            {/* Header */}
            <div className="news-header-bar">
                <h3 className="news-header-title">
                    <FileText size={20} color="#6366f1" />
                    {haberToEdit ? 'Haberi Düzenle' : 'Yeni Haber Yayınla'}
                </h3>
                <button type="button" className="news-close-btn" onClick={onCancel} aria-label="Kapat">
                    <X size={18} />
                </button>
            </div>

            {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '12px 14px', borderRadius: '12px', fontSize: '0.88rem' }}>
                    {error}
                </div>
            )}

            {/* 16:9 Cover Banner */}
            <div className="news-banner-wrap">
                <img 
                    src={formData.resimURL || "https://images.unsplash.com/photo-1585829365295-ab7cd400c167?q=80&w=1200"} 
                    alt="Haber Kapak Görseli" 
                />
                <div className="news-banner-overlay">
                    <button 
                        type="button" 
                        className="news-banner-btn"
                        onClick={() => setShowUploader(!showUploader)}
                    >
                        <Camera size={15} /> {showUploader ? 'Kapat' : 'Kapağı Değiştir'}
                    </button>
                </div>
            </div>

            {/* Collapsible Uploader */}
            {showUploader && (
                <div className="news-uploader-drawer">
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '8px' }}>
                        Haber Kapak Görseli Seçin veya Link Girin:
                    </span>
                    <ImageUploader
                        currentUrl={formData.resimURL}
                        onUploadComplete={(url) => {
                            setFormData(prev => ({ ...prev, resimURL: url }));
                            setShowUploader(false);
                        }}
                    />
                </div>
            )}

            {/* Headline Title */}
            <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '4px' }}>
                    Haber Başlığı <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                    type="text"
                    name="baslik"
                    value={formData.baslik}
                    onChange={handleChange}
                    placeholder="Haber başlığını buraya yazın..."
                    className="news-headline-input"
                    required
                />
            </div>

            {/* Metadata Bar (Date & Club) */}
            <div className="news-meta-strip">
                <div className="news-date-card">
                    <Calendar size={16} color="#6366f1" />
                    <div>
                        <span style={{ fontSize: '0.66rem', color: '#94a3b8', display: 'block', fontWeight: 700 }}>YAYIN TARİHİ</span>
                        <input
                            type="datetime-local"
                            name="tarih"
                            value={formData.tarih}
                            onChange={handleChange}
                        />
                    </div>
                </div>

                {kulup?.ad && (
                    <div className="news-club-badge">
                        <Building size={15} /> {kulup.ad}
                    </div>
                )}
            </div>

            {/* Summary / Excerpt */}
            <div className="news-section-card">
                <div className="news-section-label">
                    <Sparkles size={14} color="#6366f1" /> Kısa Özet & Giriş (Lead)
                </div>
                <AutoTextarea
                    name="aciklama"
                    minRows={2}
                    value={formData.aciklama}
                    onChange={handleChange}
                    placeholder="Haberin ana fikrini özetleyen 1-2 cümlelik dikkat çekici spot metin..."
                    style={{
                        border: '1.5px solid #e2e8f0',
                        borderRadius: '10px',
                        padding: '10px 12px',
                        background: '#f8fafc',
                        fontStyle: 'italic',
                        color: '#334155'
                    }}
                />
            </div>

            {/* Full Story Content */}
            <div className="news-section-card">
                <div className="news-section-label">
                    <AlignLeft size={14} color="#6366f1" /> Haber Detayı & Metin
                </div>
                <AutoTextarea
                    name="icerik"
                    minRows={6}
                    value={formData.icerik}
                    onChange={handleChange}
                    placeholder="Haberin tam detaylarını ve gelişmeleri buraya dilediğiniz gibi yazın..."
                    style={{
                        border: '1.5px solid #e2e8f0',
                        borderRadius: '10px',
                        padding: '14px',
                        background: '#ffffff',
                        fontSize: '15px',
                        lineHeight: '1.7',
                        color: '#0f172a'
                    }}
                />
                <div className="news-word-counter">
                    <span>{wordCount} kelime</span>
                    <span>{charCount} karakter</span>
                </div>
            </div>

            {/* Sticky Action Footer */}
            <div className="form-sticky-actions">
                <button type="button" onClick={onCancel} className="cancel-button" disabled={isSaving}>
                    <X size={16} /> Vazgeç
                </button>
                <button type="submit" className="submit-button" disabled={isSaving}>
                    <Check size={16} /> {isSaving ? 'Kaydediliyor...' : 'Haberi Yayınla'}
                </button>
            </div>
        </form>
    );
};

export default HaberFormu;